import {
	Check,
	ClipboardList,
	FilePen,
	FileSearch,
	FolderInput,
	MessagesSquare,
	Receipt,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import Seo from "../components/Seo";
import { Badge } from "../components/ui/badge";
import { buttonVariants } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { AUTH_ENABLED, authClient, WORKSPACE_PATH } from "../lib/authClient";
import { cn } from "../lib/utils";
import { SEO_ROUTE_PROPS } from "../seo/metadata";

const DEMO_BOOKING_URL = "https://cal.com/team/clerical/clerical-demo";

const steps: { title: string; description: string; href?: string }[] = [
	{
		title: "Schedule a demo",
		href: DEMO_BOOKING_URL,
		description: "We'll give you a quick tour of what Clerical can do.",
	},
	{
		title: "Onboard your firm",
		description:
			"We'll connect Clerical to your systems, your folder conventions, and your writing style.",
	},
	{
		title: "Get to work",
		description:
			"Draft emails from Outlook, or draft and edit documents from the web.",
	},
];

const automationItems = [
	{
		icon: MessagesSquare,
		title: "Draft client emails",
		description:
			"Replies drafted right in the inbox, grounded in the matter's actual status, in English, Spanish, or many other languages. Your team reviews and sends.",
	},
	{
		icon: FilePen,
		title: "Draft and edit documents",
		description:
			"Generate cover letters, affidavits, and exhibits from your templates or past petitions, matching your firm's formatting style.",
	},
	{
		icon: FileSearch,
		title: "Read and extract from documents",
		description:
			"Pull a receipt number, I-94 expiration, or passport dates out of a PDF, summarize a notice, or flag where a name, date, or job title does not match across the documents in a case.",
	},
	{
		icon: FolderInput,
		title: "File attachments to the right matter",
		description:
			"When a client sends a passport or a birth certificate, Clerical names it by your convention and saves it to the correct matter subfolder in OneDrive or SharePoint.",
	},
	{
		icon: ClipboardList,
		title: "Update case management",
		description:
			"Write case notes, set calendar deadlines, and update records in Clio or Docketwise, then keep your Excel case trackers current.",
	},
	{
		icon: Receipt,
		title: "Invoices and billing",
		description:
			"Generate client invoices, calculate filing and service fees, and keep your billing spreadsheets up to date.",
	},
];

type PricingTier = {
	name: string;
	tagline: string;
	/** Per-month price when billed annually. Null = custom / contact sales. */
	priceAnnual: number | null;
	/** Per-month price when billed monthly (higher than annual). */
	priceMonthly: number | null;
	/** Monthly token allotment. Null = custom volume. */
	tokens: number | null;
	cta: string;
	featured?: boolean;
	/** Header above the checklist, e.g. "Everything in Starter, plus". */
	featuresHeader: string;
	features: string[];
};

const pricingTiers: PricingTier[] = [
	{
		name: "Starter",
		tagline: "For firms just getting started with AI",
		priceAnnual: 49,
		priceMonthly: 59,
		tokens: 50_000,
		cta: "Get Started",
		featuresHeader: "Includes",
		features: [
			"Email & reply drafting in Outlook",
			"Document summarization and extraction",
			"Document drafting and editing",
			"Multilingual translations",
			"OneDrive, Clio, and Docketwise integrations",
			"Fast, capable models (Sonnet, Gemini, etc.)",
			"Email and Zoom support",
			"Pay-as-you-go token overages",
		],
	},
	{
		name: "Professional",
		tagline: "For firms that are ramping up their automation",
		priceAnnual: 149,
		priceMonthly: 179,
		tokens: 165_000,
		cta: "Get Started",
		featured: true,
		featuresHeader: "Everything in Starter, plus",
		features: ["Frontier models (Opus & GPT-5.5)", "Monthly token discounts"],
	},
	{
		name: "Custom",
		tagline: "For firms that want significant integration",
		priceAnnual: null,
		priceMonthly: null,
		tokens: null,
		cta: "Contact Sales",
		featuresHeader: "Everything in Professional, plus",
		features: [
			"Custom integrations (case management software, cloud storage, etc.)",
			"Custom workflow development (document and automate existing processes)",
			"Dedicated onboarding and training",
			"Priority support",
		],
	},
];

export default function LandingPage() {
	const session = authClient.useSession();
	const isLoaded = !session.isPending;
	const isSignedIn = !!session.data;
	const showSignedIn = AUTH_ENABLED && isLoaded && isSignedIn;

	const [billingCycle, setBillingCycle] = useState<"annual" | "monthly">(
		"annual",
	);

	return (
		<div className="landing">
			<Seo {...SEO_ROUTE_PROPS["/"]} />

			{/* ── 1. Hero — light cream background with rose-accent mist gradient ── */}
			<section className="landing-hero-section relative overflow-hidden">
				{/* Mist gradient — pine base + rose wisp, flowing swoop from bottom-left */}
				<svg
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 z-0 h-full w-full"
					viewBox="0 0 1500 520"
					preserveAspectRatio="none"
				>
					<defs>
						<linearGradient
							id="hero-mist-base"
							x1="0%"
							y1="100%"
							x2="100%"
							y2="0%"
						>
							<stop
								offset="0%"
								stopColor="oklch(0.22 0.05 163)"
								stopOpacity="0.24"
							/>
							<stop
								offset="45%"
								stopColor="oklch(0.36 0.06 163)"
								stopOpacity="0.14"
							/>
							<stop
								offset="100%"
								stopColor="oklch(0.38 0.06 175)"
								stopOpacity="0"
							/>
						</linearGradient>
						<linearGradient
							id="hero-mist-rose"
							x1="0%"
							y1="100%"
							x2="100%"
							y2="20%"
						>
							<stop
								offset="0%"
								stopColor="oklch(0.76 0.08 18)"
								stopOpacity="0.22"
							/>
							<stop
								offset="60%"
								stopColor="oklch(0.84 0.06 12)"
								stopOpacity="0.12"
							/>
							<stop
								offset="100%"
								stopColor="oklch(0.84 0.06 12)"
								stopOpacity="0"
							/>
						</linearGradient>
						<filter id="hero-mist-blur">
							<feGaussianBlur stdDeviation="2" />
						</filter>
					</defs>
					<path
						d="M-50 520 Q 100 480 320 476 T 700 440 T 1080 380 T 1580 280 L 1580 520 Z"
						fill="url(#hero-mist-base)"
						filter="url(#hero-mist-blur)"
					/>
					<path
						d="M-50 520 Q 150 460 380 452 T 780 400 T 1180 320 T 1580 190 L 1580 300 Q 1180 420 780 470 T 380 500 T -50 520 Z"
						fill="url(#hero-mist-rose)"
						filter="url(#hero-mist-blur)"
						opacity="0.8"
					/>
				</svg>
				<div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center gap-5 px-4 py-14 text-center sm:px-6 lg:py-20 lg:px-8">
					<h1 className="landing-title max-w-3xl text-[39px] font-medium leading-[1.04] text-foreground sm:text-[50px] lg:text-[62px]">
						Automate your admin, work smarter, live better.
					</h1>
					<p className="max-w-lg text-sm leading-7 text-muted-foreground sm:text-base">
						Use Clerical&apos;s AI to quickly draft emails, prepare cover
						letters and exhibits, and assemble petitions. Integrates with your
						current systems and processes. Less admin, more law.
					</p>
					<div className="flex flex-col gap-3 sm:flex-row">
						{showSignedIn ? (
							<Link
								to={WORKSPACE_PATH}
								className={cn(
									buttonVariants({ size: "lg" }),
									"bg-pine-forest text-cream hover:bg-pine-ink!",
								)}
							>
								Open Dashboard
							</Link>
						) : (
							<a
								href={DEMO_BOOKING_URL}
								className={cn(
									buttonVariants({ size: "lg" }),
									"bg-pine-forest text-cream hover:bg-pine-ink!",
								)}
							>
								Get Started
							</a>
						)}
					</div>
				</div>
			</section>

			{/* ── 2. What We Automate — pine-green anchor section ── */}
			<section
				className="landing-automation-section py-12 lg:py-20"
				id="automate"
			>
				<div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:px-8">
					<h2 className="landing-section-title text-[36px] leading-[1.06] text-cream sm:text-[42px]">
						What we automate
					</h2>
					<div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
						{automationItems.map((item, index) => {
							const Icon = item.icon;
							const iconTone =
								index % 3 === 1
									? "bg-copper/15 text-copper-dark"
									: "bg-pine-forest/10 text-pine-forest";

							return (
								<Card
									key={item.title}
									className="rounded-lg border-cream/20 bg-cream/95 shadow-[0_22px_70px_rgba(12,38,30,0.16)] ring-1 ring-cream/25"
								>
									<CardContent className="flex flex-col gap-4 p-6 lg:p-7">
										<div
											className={cn(
												"flex size-10 items-center justify-center rounded-lg",
												iconTone,
											)}
										>
											<Icon className="size-[1.1rem]" />
										</div>
										<h3 className="text-lg font-semibold tracking-tight text-foreground">
											{item.title}
										</h3>
										<p className="text-sm leading-7 text-muted-foreground">
											{item.description}
										</p>
									</CardContent>
								</Card>
							);
						})}
					</div>
				</div>
			</section>

			{/* ── 3. How It Works — cream background ── */}
			<section className="bg-background py-12 lg:py-20" id="how">
				<div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:px-8">
					<h2 className="landing-section-title text-center text-[36px] leading-[1.06] sm:text-[42px]">
						How it works
					</h2>
					<ol className="mx-auto flex w-full max-w-2xl flex-col">
						{steps.map((step, index) => (
							<li
								key={step.title}
								className="relative flex gap-5 pb-10 last:pb-0"
							>
								{index < steps.length - 1 && (
									<span
										aria-hidden="true"
										className="absolute top-[1.375rem] left-[1.375rem] h-full w-px -translate-x-1/2 bg-pine-forest/20"
									/>
								)}
								<span className="relative z-10 flex size-11 shrink-0 items-center justify-center rounded-full border border-pine-forest/25 bg-card font-heading text-xl text-pine-forest">
									{index + 1}
								</span>
								<div className="pt-1">
									<h3 className="landing-section-title text-xl text-foreground sm:text-2xl">
										{step.href ? (
											<a
												href={step.href}
												className="text-pine-forest underline-offset-4 transition-colors hover:underline"
											>
												{step.title}
											</a>
										) : (
											step.title
										)}
									</h3>
									<p className="mt-2 max-w-md text-base leading-7 text-muted-foreground">
										{step.description}
									</p>
								</div>
							</li>
						))}
					</ol>
				</div>
			</section>

			{/* ── 4. Pricing — warm beige background, annual/monthly toggle ── */}
			<section className="landing-pricing-section py-12 lg:py-20" id="pricing">
				<div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
						<h2 className="landing-section-title text-[36px] leading-[1.06] sm:text-[42px]">
							Pricing
						</h2>

						{/* Billing toggle */}
						<fieldset
							className="m-0 inline-flex items-center rounded-full border border-border bg-background p-1"
							aria-label="Billing period"
						>
							<button
								type="button"
								aria-pressed={billingCycle === "monthly"}
								onClick={() => setBillingCycle("monthly")}
								className={cn(
									"rounded-full px-5 py-1.5 text-sm font-medium transition-colors",
									billingCycle === "monthly"
										? "bg-pine-forest text-cream"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								Pay Monthly
							</button>
							<button
								type="button"
								aria-pressed={billingCycle === "annual"}
								onClick={() => setBillingCycle("annual")}
								className={cn(
									"flex items-center gap-2 rounded-full px-5 py-1.5 text-sm font-medium transition-colors",
									billingCycle === "annual"
										? "bg-pine-forest text-cream"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								Pay Annually
								<span
									className={cn(
										"text-xs",
										billingCycle === "annual"
											? "text-cream/70"
											: "text-muted-foreground/70",
									)}
								>
									Save 17%
								</span>
							</button>
						</fieldset>
					</div>

					<div className="mt-12 grid gap-6 lg:grid-cols-3">
						{pricingTiers.map((tier) => {
							const isCustom = tier.priceAnnual === null;
							const price =
								billingCycle === "annual"
									? tier.priceAnnual
									: tier.priceMonthly;

							return (
								<Card
									key={tier.name}
									className={cn(
										"relative flex flex-col border-border/50 pb-0 shadow-sm",
										tier.featured &&
											"overflow-visible border-pine-forest shadow-md ring-2 ring-pine-forest/20",
									)}
								>
									{tier.featured && (
										<Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-pine-forest px-3 py-1 text-cream hover:bg-pine-forest">
											Most popular
										</Badge>
									)}
									<CardContent className="flex flex-1 flex-col gap-6 p-7">
										<div>
											<h3 className="text-xl font-semibold leading-tight text-foreground">
												{tier.name}
											</h3>
											<p className="mt-1 text-sm leading-6 text-muted-foreground">
												{tier.tagline}
											</p>
										</div>

										<div>
											<div className="flex items-baseline gap-1.5">
												{isCustom ? (
													<span className="text-3xl font-medium tracking-normal text-foreground">
														Custom
													</span>
												) : (
													<>
														<span className="text-3xl font-medium tracking-normal text-foreground">
															${price}
														</span>
														<span className="text-sm text-muted-foreground">
															/ month
														</span>
													</>
												)}
											</div>
											<p className="mt-1 h-4 text-xs text-muted-foreground">
												{isCustom
													? "Tailored to your practice"
													: billingCycle === "annual"
														? "billed annually"
														: "billed monthly"}
											</p>
										</div>

										<a
											href={DEMO_BOOKING_URL}
											className={cn(
												buttonVariants({
													size: "lg",
													variant: tier.featured ? "default" : "outline",
												}),
												"w-full",
												tier.featured &&
													"bg-pine-forest text-cream hover:bg-pine-ink!",
											)}
										>
											{tier.cta}
										</a>

										{/* Rule below the CTA, mirroring the credits band's top border */}
										<div className="-mx-7 border-t border-border/60" />

										{/* Feature checklist — grows so the credits band pins to the bottom */}
										<div className="flex flex-1 flex-col gap-4">
											<p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
												{tier.featuresHeader}
											</p>
											<ul className="flex flex-col gap-3">
												{tier.features.map((feature) => (
													<li
														key={feature}
														className="flex items-start gap-2.5 text-sm leading-6 text-foreground"
													>
														<Check className="mt-0.5 size-4 shrink-0 text-pine-forest" />
														<span>{feature}</span>
													</li>
												))}
											</ul>
										</div>

										{/* Credits band — set apart at the bottom, town-style */}
										<div
											className={cn(
												"-mx-7 -mb-7 mt-2 rounded-b-xl border-t border-border/60 px-7 py-5",
												tier.featured ? "bg-pine-forest/[0.06]" : "bg-muted/40",
											)}
										>
											{tier.tokens === null ? (
												<>
													<div className="text-xl font-medium tracking-normal text-foreground">
														Custom volume
													</div>
													<p className="mt-0.5 text-xs text-muted-foreground">
														Token allotment sized to your caseload
													</p>
												</>
											) : (
												<>
													<div className="flex items-baseline gap-2">
														<span className="text-xl font-medium tracking-normal text-foreground">
															{tier.tokens.toLocaleString()}
														</span>
														<span className="text-sm text-muted-foreground">
															tokens
														</span>
													</div>
													<p className="mt-0.5 text-xs text-muted-foreground">
														included monthly
													</p>
												</>
											)}
										</div>
									</CardContent>
								</Card>
							);
						})}
					</div>
				</div>
			</section>

			{/* ── 5. CTA — pine forest full-width ── */}
			<section className="bg-pine-forest py-12 lg:py-20" id="cta">
				<div className="mx-auto w-full max-w-7xl px-4 text-center sm:px-6 lg:px-8">
					<p className="mx-auto max-w-xl text-base leading-7 text-cream/85">
						We&apos;ll show you exactly how Clerical works for firms like yours.
					</p>
					<div className="mt-8">
						{showSignedIn ? (
							<Link
								to={WORKSPACE_PATH}
								className={cn(
									buttonVariants({ size: "lg" }),
									"bg-cream text-pine-forest hover:bg-cream-dark!",
								)}
							>
								Open Dashboard
							</Link>
						) : (
							<a
								href={DEMO_BOOKING_URL}
								className={cn(
									buttonVariants({ size: "lg" }),
									"bg-cream text-pine-forest hover:bg-cream-dark!",
								)}
							>
								Get Started
							</a>
						)}
					</div>
				</div>
			</section>
		</div>
	);
}
